import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://www.sthanick.com";
const DEFAULT_TITLE =
  "Sthanick: India's Trusted Hyperlocal Discovery and E-commerce Platform";
const DEFAULT_DESCRIPTION =
  "Discover and shop from your favorite local stores with Sthanick. Get groceries, daily essentials, and pharmacy items delivered fast right to your doorstep.";
const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;

const serviceMeta = {
  plumbers: {
    title: "Plumbers Near Me | Sthanick Local Services",
    description:
      "Find plumbers near you on Sthanick. Discover nearby plumbing service providers for home repairs, pipe work, fittings and maintenance.",
  },
  "plumbing-services": {
    title: "Plumbing Services Near Me | Sthanick",
    description:
      "Search local plumbing services near you on Sthanick and connect with nearby service providers for home and commercial repairs.",
  },
  electricians: {
    title: "Electricians Near Me | Sthanick Local Services",
    description:
      "Find electricians near you on Sthanick for wiring, electrical repairs, appliance connections and local maintenance needs.",
  },
  "pest-control": {
    title: "Pest Control Near Me | Sthanick Local Services",
    description:
      "Discover pest control providers near you on Sthanick for home, shop and office pest treatment services.",
  },
  "interior-designers": {
    title: "Interior Designers Near Me | Sthanick",
    description:
      "Find nearby interior designers and home improvement service providers through Sthanick.",
  },
  "real-estate-agents": {
    title: "Real Estate Agents Near Me | Sthanick",
    description:
      "Find real estate agents near you on Sthanick for rentals, residential properties and local property assistance.",
  },
  "banquet-halls": {
    title: "Banquet Halls Near Me | Sthanick",
    description:
      "Discover banquet halls and event venues near you through Sthanick local listings.",
  },
};

const categoryMeta = {
  groceries: {
    title: "Grocery Stores Near Me | Sthanick Nearby Shops",
    description:
      "Find nearby grocery stores, daily essentials, milk, food products and local sellers on Sthanick.",
  },
  electronics: {
    title: "Electronics Stores Near Me | Sthanick Nearby Shops",
    description:
      "Discover nearby electronics stores and local vendors for gadgets, devices and accessories on Sthanick.",
  },
  "home-appliances": {
    title: "Home Appliance Stores Near Me | Sthanick",
    description:
      "Find nearby home appliance stores and local vendors for appliances, electronics and household needs.",
  },
  vehicles: {
    title: "Vehicle Dealers and Services Near Me | Sthanick",
    description:
      "Find nearby vehicle sellers, dealers and related local services through Sthanick.",
  },
  "home-decor": {
    title: "Home Decor Stores Near Me | Sthanick",
    description:
      "Discover nearby home decor shops, furniture, interiors and local home improvement vendors on Sthanick.",
  },
  furniture: {
    title: "Furniture Stores Near Me | Sthanick",
    description:
      "Find furniture stores near you and shop from local vendors through Sthanick.",
  },
  stationery: {
    title: "Stationery Stores Near Me | Sthanick",
    description:
      "Discover stationery shops near you and other local sellers on Sthanick.",
  },
};

const staticMeta = {
  "/": {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  },
  "/about-us": {
    title: "About Sthanick | Hyperlocal Shops and Services",
    description:
      "Learn about Sthanick, a hyperlocal platform connecting customers with nearby shops, local vendors and service providers.",
  },
  "/see-allnearbyShopsSection": {
    title: "Nearby Shops Near Me | Sthanick",
    description:
      "Browse nearby shops and local vendors on Sthanick for groceries, electronics, home appliances, daily essentials and more.",
  },
  "/see-allTopListSearches": {
    title: "Nearby Services Near Me | Sthanick",
    description:
      "Browse nearby service providers on Sthanick, including plumbers, electricians, pest control, interiors, real estate agents and more.",
  },
  "/help-center": {
    title: "Help Center | Sthanick",
    description:
      "Get help with Sthanick orders, nearby shops, service providers, account support and vendor support.",
  },
  "/privacy-policy": {
    title: "Privacy Policy | Sthanick",
    description:
      "Read the Sthanick privacy policy for customers, vendors, delivery partners and service providers.",
  },
  "/termsconditions": {
    title: "Terms and Conditions | Sthanick",
    description:
      "Read the terms and conditions for using Sthanick's nearby shops, local vendors and service provider platform.",
  },
  "/vendor-agreement": {
    title: "Vendor Agreement | Sthanick",
    description:
      "Review the Sthanick vendor agreement for merchants, self-delivery sellers and service providers.",
  },
};

const humanizeSlug = (slug = "") =>
  slug
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const setMeta = (selector, attribute, value) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    const nameMatch = selector.match(/meta\[name="([^"]+)"\]/);
    const propertyMatch = selector.match(/meta\[property="([^"]+)"\]/);

    if (nameMatch) element.setAttribute("name", nameMatch[1]);
    if (propertyMatch) element.setAttribute("property", propertyMatch[1]);

    document.head.appendChild(element);
  }

  element.setAttribute(attribute, value);
};

const setCanonical = (url) => {
  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }
  canonical.setAttribute("href", url);
};

const getRouteMeta = (pathname) => {
  const normalizedPath = pathname.replace(/\/+$/, "") || "/";
  const parts = normalizedPath.split("/").filter(Boolean);

  if (parts[0] === "toplist" && parts[1]) {
    const slug = parts[1].toLowerCase();
    return (
      serviceMeta[slug] || {
        title: `${humanizeSlug(slug)} Near Me | Sthanick Local Services`,
        description: `Find ${humanizeSlug(slug).toLowerCase()} near you on Sthanick and connect with nearby local service providers.`,
      }
    );
  }

  if (parts[0] === "category" && parts[1]) {
    const slug = parts[1].toLowerCase();
    return (
      categoryMeta[slug] || {
        title: `${humanizeSlug(slug)} Near Me | Sthanick Nearby Shops`,
        description: `Find ${humanizeSlug(slug).toLowerCase()} shops and local vendors near you on Sthanick.`,
      }
    );
  }

  // /shop/:city/:category/:slug  where slug = "<shop-name>-<id>"
  if ((parts[0] === "shop" || parts[0] === "vendorshop") && parts[3]) {
    const decode = (part) => {
      try {
        return decodeURIComponent(part);
      } catch {
        return part;
      }
    };
    const shopName = humanizeSlug(decode(parts[3]).replace(/-?\d+$/, ""));
    const city = parts[1] !== "unknown" ? humanizeSlug(decode(parts[1])) : "";
    const category =
      parts[2] !== "unknown" ? humanizeSlug(decode(parts[2])) : "";
    const name = shopName || "Local Vendor";
    const place = city ? ` in ${city}` : "";

    return {
      title: `${name}${city ? `, ${city}` : ""}${category ? ` - ${category}` : ""} | Sthanick`,
      description: `${name}${place} on Sthanick${category ? ` (${category.toLowerCase()})` : ""}. View shop details, address, timings, phone number, products and directions.`,
    };
  }

  return staticMeta[normalizedPath] || staticMeta["/"];
};

const SeoUpdater = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const { title, description } = getRouteMeta(pathname);
    const url = `${SITE_URL}${pathname === "/" ? "/" : pathname}`;

    document.title = title;
    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[property="og:image"]', "content", DEFAULT_IMAGE);
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", description);
    setMeta('meta[name="twitter:image"]', "content", DEFAULT_IMAGE);
    setCanonical(url);
  }, [pathname]);

  return null;
};

export default SeoUpdater;
